/**
 * Missions Slice
 *
 * GW-5445 (P1-11): Zustand slice that stores a user's missions keyed by scope
 * (personal | org | admin). Each scope has its own mission list, status, and
 * shared filters.
 *
 * GW-10472: calls `GET /v1/missions` on the gw-missions service and adapts
 * the response to `MissionSummary` records scoped to 'personal' (the current
 * scope). The fixture-mode short-circuit this slice previously had was
 * removed -- /personal/missions is fully live-API-backed now.
 */

import type { MissionListItem } from '@/api/missionsDirectApiClient';
import { missionsDirectApi } from '@/api/missionsDirectApiClient';

import type { MissionSummary, RunStatus } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

/**
 * GW-10328 (gw-ui 3.23.2/GW-10231) / GW-10160 (gw-ui 3.24.0): translate the
 * backend mission-level `MissionStatus` vocabulary (draft/briefing/confirmed/
 * queued/running/complete/failed/cancelled/awaiting_decision — see
 * gw-go-common/models/mission.go and the missions-api OpenAPI `MissionStatus`
 * enum) into the canonical run-level `RunStatus` vocabulary
 * (created/launching/running/paused/completed/failed/cancelled/stopped) that
 * `MissionSummary.status` now carries.
 *
 * The two vocabularies do NOT overlap 1:1 — notably backend `complete` maps
 * to `completed`, and `awaiting_decision` maps to `paused` (runStatusConfig.ts
 * documents that a paused-equivalent must be mapped to `paused` before
 * display). Pre-run lifecycle states (draft/briefing/confirmed) collapse to
 * `created`; `queued` maps to `launching` (submitted to the runner). Unknown
 * values fall back to `created`, matching getRunStatusConfig's own fallback.
 *
 * This is only the best-effort DEFAULT shown before a mission's latest run's
 * real status has loaded (or for a mission with zero runs) — MissionListPage
 * overwrites it with the actual `Run.status` once `runsByMissionId` has that
 * mission's runs.
 */
const MISSION_STATUS_TO_RUN_STATUS: Record<string, RunStatus> = {
  draft: 'created',
  briefing: 'created',
  confirmed: 'created',
  queued: 'launching',
  running: 'running',
  awaiting_decision: 'paused',
  complete: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
};

function toRunStatus(missionStatus: string): RunStatus {
  return MISSION_STATUS_TO_RUN_STATUS[missionStatus] ?? 'created';
}

export type MissionScope = 'personal' | 'org' | 'admin';
type StatusValue = 'idle' | 'loading' | 'ready' | 'error';
type StatusMap = Record<MissionScope, StatusValue>;
type MissionsMap = Record<MissionScope, MissionSummary[]>;

const FETCH_PAGE_SIZE = 20;

export interface MissionsSlice {
  missionsByScope: MissionsMap;
  missionsStatusByScope: StatusMap;
  /** Raw wire-format Mission.status (MissionStatus vocabulary) — GW-10231/GW-10160's status-canonicalization only touched MissionSummary.status, not this pre-canonicalization filter shape. */
  missionsFilters: { status?: string; missionType?: string };
  /**
   * GW-10231: loads the FULL mission list for a scope — looping GET
   * /v1/missions?offset=N (limit=20 per request) until every page has been
   * fetched — rather than stopping at the first 20-item page. MissionListPage
   * paginates the resulting in-memory array client-side via
   * `DataTablePagination`, mirroring how that component's own story feeds it
   * a pre-sliced array rather than refetching per page.
   */
  fetchMissionsForScope: (scope: MissionScope) => Promise<void>;
  setMissionsFilters: (filters: Partial<MissionsSlice['missionsFilters']>) => void;
}

const emptyMap = <T>(init: T): Record<MissionScope, T> => ({
  personal: init,
  org: init,
  admin: init,
});

const initial = {
  missionsByScope: emptyMap<MissionSummary[]>([]),
  missionsStatusByScope: emptyMap<StatusValue>('idle'),
  missionsFilters: {} as MissionsSlice['missionsFilters'],
};

/** Truncates a mission's prompt to a short row-label snippet — the "Mission"
 * column's default displayName source (GW-10160). Cuts on a word boundary
 * where possible rather than mid-word. */
function promptSnippet(prompt: string, maxLength = 60): string {
  const trimmed = prompt.trim();
  if (trimmed.length <= maxLength) return trimmed;
  const cut = trimmed.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

/**
 * Adapt a raw API mission list item to the MissionSummary shape expected by
 * the store and UI components. Fields absent from the API response are filled
 * with sensible defaults so the UI never receives undefined where it expects
 * a concrete value.
 */
function adaptToMissionSummary(item: MissionListItem, scope: MissionScope): MissionSummary {
  return {
    id: item.missionId,
    missionId: item.missionType,
    shortName: item.shortName ?? item.missionType,
    // GW-10160: the Mission column previously showed "{missionType} — {target
    // name}" — effectively just the mission type again, duplicating the
    // adjacent Type column. A snippet of the mission's own prompt is more
    // useful for distinguishing rows at a glance.
    displayName: item.displayName ?? promptSnippet(item.prompt) ?? item.missionType,
    status: toRunStatus(item.status),
    createdAt: item.createdAt,
    updatedAt: item.updatedAt ?? item.createdAt,
    // GW-11906: no per-mission estimate range exists on the wire, so this is a
    // last-resort fallback only. The MissionListTable "Total Cost" column
    // renders tokensConsumed/tokensUsed (a single "N tokens" badge) ahead of
    // this range — with the real aggregate now flowing into tokensConsumed
    // (below), the badge no longer falls back to a hardcoded "0-0 tokens".
    tokenEstimate: { min: 0, max: 0 },
    // GW-11906 (Epic GW-11903): GET /v1/missions now carries
    // totalCostAllRunsTokens — the aggregate settled SMART-token spend across
    // every run of this mission (gw-missions-api Wave-2, GW-11905). Feed it
    // into tokensConsumed so the list "Total Cost" badge shows real spend
    // instead of the former hardcoded 0-0 estimate. An explicit per-mission
    // tokensConsumed still wins when the wire ever carries one.
    tokensConsumed: item.tokensConsumed ?? item.totalCostAllRunsTokens ?? null,
    tokensUsed: item.tokensUsed ?? null,
    requiresHitl: item.requiresHitl ?? false,
    // GW-10160: derive from the real `runIds[]` field (oldest-first) rather
    // than the fictional `runCount`/`latestRunId` fields, which do not exist
    // on the deployed Mission schema and are always undefined on the wire.
    runCount: item.runIds?.length ?? item.runCount ?? 0,
    latestRunId: item.runIds?.[item.runIds.length - 1] ?? item.latestRunId ?? null,
    productCount: item.productCount ?? 0,
    scope,
  };
}

export const createMissionsSlice: StateCreator<MissionsSlice, [], [], MissionsSlice> = (
  set,
  get
) => ({
  ...initial,
  fetchMissionsForScope: async (scope) => {
    set({ missionsStatusByScope: { ...get().missionsStatusByScope, [scope]: 'loading' } });
    try {
      const allItems: MissionListItem[] = [];
      let offset = 0;
      let total = Infinity;
      while (offset < total) {
        const response = await missionsDirectApi.listMissions({
          limit: FETCH_PAGE_SIZE,
          offset,
        });
        allItems.push(...response.missions);
        total = response.total;
        offset += FETCH_PAGE_SIZE;
      }
      const missions = allItems.map((item) => adaptToMissionSummary(item, scope));
      set({
        missionsByScope: { ...get().missionsByScope, [scope]: missions },
        missionsStatusByScope: { ...get().missionsStatusByScope, [scope]: 'ready' },
      });
    } catch (err) {
      set({
        missionsStatusByScope: { ...get().missionsStatusByScope, [scope]: 'error' },
      });
      throw err;
    }
  },
  setMissionsFilters: (filters) =>
    set({ missionsFilters: { ...get().missionsFilters, ...filters } }),
});
